import * as React from "react";
import { CalendarClock, Download } from "lucide-react";
import { useSearchParams } from "react-router";

import { useGetRenewalFamiliesQuery } from "@/api/renewals";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { MultiSelectFilter } from "@/components/multi-select-filter";
import { getUserDisplayName } from "@/helpers/userDisplay";
import { cn } from "@/lib/utils";
import type { ActionBucket, RenewalFamily } from "@/types/renewals";

import { FilterSelect } from "./components/filter-select";
import { RenewalBoard } from "./components/renewal-board";
import { RenewalTable } from "./components/renewal-table";
import { ACTION_BUCKET_LABELS, SORT_LABELS, STATUS_LABELS } from "./constants";
import { downloadRenewalsCsv, downloadRenewalsWorkbook, renewalsCsv, renewalsWorkbook } from "./renewal-export";
import { filterRenewalFamilies, type RenewalFilters, type RenewalSort, sortRenewalFamilies } from "./renewal-filters";
import { useUserDirectory } from "./use-owner-labels";

type View = "board" | "table";

const FILTER_PARAMS: Record<keyof RenewalFilters, string> = {
  search: "search",
  categoryL2: "l2",
  categoryL3: "l3",
  ownerId: "owner",
  termination: "termination",
  actionDue: "notice",
  status: "status",
};

// Filters each view offers. The board groups by notice date, so "notice due" is a table
// filter; the specification lists L2 for the board only.
const FILTERS_BY_VIEW: Record<View, ReadonlySet<keyof RenewalFilters>> = {
  board: new Set(["search", "categoryL2", "categoryL3", "ownerId", "termination", "status"]),
  table: new Set(["search", "categoryL3", "ownerId", "termination", "status", "actionDue"]),
};

const labelled = <T extends string>(labels: Record<T, string>) =>
  (Object.keys(labels) as T[]).map(value => ({ value, label: labels[value] }));

const distinct = (values: Array<string | null>) =>
  [...new Set(values.filter((value): value is string => Boolean(value)))]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
    .map(value => ({ value, label: value }));

// Every filter except search may repeat its URL parameter, once per picked value.
const readFilters = (params: URLSearchParams, view: View) =>
  Object.fromEntries(
    (Object.entries(FILTER_PARAMS) as Array<[keyof RenewalFilters, string]>).map(([key, param]) => {
      const offered = FILTERS_BY_VIEW[view].has(key);
      if (key === "search") return [key, offered ? (params.get(param) ?? "") : ""];
      return [key, offered ? params.getAll(param).filter(Boolean) : []];
    }),
  ) as unknown as RenewalFilters;

const readSort = (params: URLSearchParams): RenewalSort => {
  const sort = params.get("sort");
  return sort && sort in SORT_LABELS ? (sort as RenewalSort) : "notice";
};

export const RenewalCentrePage = () => {
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "table" ? "table" : "board";
  const filters = readFilters(params, view);
  // Typing in search rewrites the URL per keystroke; filtering follows at lower priority.
  const deferredQuery = React.useDeferredValue(params.toString());
  const appliedFilters = React.useMemo(
    () => readFilters(new URLSearchParams(deferredQuery), view),
    [deferredQuery, view],
  );
  const sort = readSort(params);
  const { data, isLoading, isError, refetch } = useGetRenewalFamiliesQuery();
  const families = React.useMemo(() => data?.items ?? [], [data?.items]);
  const owners = useUserDirectory(React.useMemo(() => families.map(family => family.owner_user_id), [families]));

  const ownerLabel = React.useCallback(
    (ownerUserId: string | null) => {
      const owner = ownerUserId ? owners.get(ownerUserId) : undefined;
      return owner ? getUserDisplayName(owner) : null;
    },
    [owners],
  );
  const rows = React.useMemo(
    () => sortRenewalFamilies(filterRenewalFamilies(families, appliedFilters), sort),
    [families, appliedFilters, sort],
  );
  const options = React.useMemo(
    () => ({
      categoryL2: distinct(families.map(family => family.category_l2)),
      categoryL3: distinct(families.map(family => family.category_l3)),
      termination: distinct(families.map(family => family.termination_type)),
      owner: distinct(families.map(family => family.owner_user_id))
        .map(({ value }) => ({ value, label: ownerLabel(value) ?? value }))
        .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" })),
    }),
    [families, ownerLabel],
  );

  const update = (changes: Record<string, string | string[]>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => {
          next.delete(key);
          (Array.isArray(value) ? value : [value]).filter(Boolean).forEach(item => next.append(key, item));
        });
        return next;
      },
      { replace: true },
    );
  const setFilter = (key: keyof RenewalFilters) => (value: string | string[]) =>
    update({ [FILTER_PARAMS[key]]: value });
  const offers = (key: keyof RenewalFilters) => FILTERS_BY_VIEW[view].has(key);

  const exportOwner = (ownerUserId: string | null) => (ownerUserId ? (owners.get(ownerUserId)?.email ?? "") : "");
  const exportCsv = (selected: RenewalFamily[]) =>
    downloadRenewalsCsv(renewalsCsv(selected, exportOwner), data?.as_of ?? "");
  const exportWorkbook = async (selected: RenewalFamily[]) =>
    downloadRenewalsWorkbook(await renewalsWorkbook(selected, exportOwner), data?.as_of ?? "");

  return (
    // The board view is exactly one visible screen tall (the negative margins cancel the root
    // layout's padding), so the board, not the page, scrolls and keeps its swimlane headers in view.
    <main
      className={cn(
        "-mx-4 -my-8 bg-[#f7f3ee] px-5 py-6 sm:-mx-6 lg:-mx-8 lg:px-8 lg:py-7",
        view === "board" ? "flex h-dvh flex-col" : "min-h-screen",
      )}
      aria-label="Renewal Centre workspace"
    >
      <div className={cn("mx-auto w-full max-w-[1600px]", view === "board" && "flex min-h-0 flex-1 flex-col")}>
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-desert-sand-700">
              <CalendarClock className="size-4" aria-hidden="true" /> Renewal Centre
            </div>
            <h1 className="text-3xl font-bold leading-tight text-[#111b35]">Contract Renewals</h1>
            <p className="mt-1 max-w-2xl text-sm font-medium text-gray-500">
              {view === "board"
                ? "Grouped by time to notice deadline — act on the left, plan ahead on the right."
                : "Sorted by urgency — days until the renewal or termination notice deadline."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-[#ddd5cb] bg-white p-0.5" role="tablist" aria-label="View">
              {(["board", "table"] as const).map(option => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={view === option}
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-semibold capitalize",
                    view === option ? "bg-[#111b35] text-white" : "text-gray-600 hover:text-[#111b35]",
                  )}
                  onClick={() => update({ view: option === "board" ? "" : option })}
                >
                  {option}
                </button>
              ))}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" disabled={rows.length === 0}>
                  <Download className="mr-2 size-4" aria-hidden="true" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void exportWorkbook(rows)}>Excel (.xlsx)</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportCsv(rows)}>CSV (.csv)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Renewal filters">
          <Input
            className="h-10 min-w-56 flex-1 bg-white"
            placeholder="Search supplier or family"
            aria-label="Search supplier or family"
            value={filters.search}
            onChange={event => setFilter("search")(event.target.value)}
          />
          {offers("categoryL2") ? (
            <MultiSelectFilter
              label="L2 category"
              allLabel="L2 category: All"
              values={filters.categoryL2}
              options={options.categoryL2}
              onChange={setFilter("categoryL2")}
            />
          ) : null}
          <MultiSelectFilter
            label="L3 category"
            allLabel="L3 category: All"
            values={filters.categoryL3}
            options={options.categoryL3}
            onChange={setFilter("categoryL3")}
          />
          <MultiSelectFilter
            label="Contract owner"
            allLabel="All owners"
            values={filters.ownerId}
            options={options.owner}
            onChange={setFilter("ownerId")}
          />
          <MultiSelectFilter
            label="Termination type"
            allLabel="Termination: All"
            values={filters.termination}
            options={[...options.termination, { value: "unknown", label: "Unknown" }]}
            onChange={setFilter("termination")}
          />
          <MultiSelectFilter
            label="Status"
            allLabel="Status: All"
            values={filters.status}
            options={labelled(STATUS_LABELS)}
            onChange={setFilter("status")}
          />
          {offers("actionDue") ? (
            <MultiSelectFilter
              label="Notice due"
              allLabel="Notice due: All"
              values={filters.actionDue}
              options={labelled<ActionBucket>(ACTION_BUCKET_LABELS)}
              onChange={setFilter("actionDue")}
            />
          ) : null}
          <FilterSelect
            label="Sort"
            value={sort}
            options={(Object.keys(SORT_LABELS) as RenewalSort[]).map(value => ({
              value,
              label: `Sort: ${SORT_LABELS[value]}`,
            }))}
            onChange={value => update({ sort: value === "notice" ? "" : value })}
          />
        </div>

        {isLoading ? <p className="mt-8 text-sm text-gray-500">Loading Contract Families…</p> : null}
        {isError ? (
          <div className="mt-8 flex items-center gap-3 text-sm text-red-700" role="alert">
            Contract renewals could not be loaded.
            <Button type="button" size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : null}
        {data && view === "board" ? (
          <RenewalBoard
            families={rows}
            ownerLabel={ownerLabel}
            onViewInTable={bucket => update({ view: "table", [FILTER_PARAMS.actionDue]: bucket })}
          />
        ) : null}
        {data && view === "table" ? <RenewalTable families={rows} ownerLabel={ownerLabel} /> : null}
      </div>
    </main>
  );
};
